// composables/useHorizontalScroll.js
import { ref, computed, watch, onMounted, onUnmounted } from "vue";

export function useHorizontalScroll() {
  // Refs
  const containerRef = ref(null);
  const scrollLeft = ref(0);
  const scrollWidth = ref(0);
  const clientWidth = ref(0);
  const isDragging = ref(false);

  // Drag state
  let isDown = false;
  let startX = 0;
  let startScrollLeft = 0;

  // Touch state
  let touchStartX = 0;
  let touchStartScrollLeft = 0;

  // Computed
  const scrollPercentage = computed(() => {
    const maxScroll = scrollWidth.value - clientWidth.value;
    return maxScroll > 0 ? (scrollLeft.value / maxScroll) * 100 : 0;
  });

  const canScrollLeft = computed(() => scrollLeft.value > 0);
  const canScrollRight = computed(
    () => scrollLeft.value < scrollWidth.value - clientWidth.value - 20,
  );

  // Methods
  const updateScrollInfo = () => {
    if (!containerRef.value) return;

    scrollLeft.value = containerRef.value.scrollLeft;
    scrollWidth.value = containerRef.value.scrollWidth;
    clientWidth.value = containerRef.value.clientWidth;
  };

  const scrollTo = (amount) => {
    if (!containerRef.value) return;

    containerRef.value.scrollBy({
      left: amount,
      behavior: "smooth",
    });
  };

  const scrollToPosition = (position) => {
    if (!containerRef.value) return;

    containerRef.value.scrollTo({
      left: position,
      behavior: "smooth",
    });
  };

  // Event Handlers
  const handleScroll = () => {
    updateScrollInfo();
  };

  const handleWheel = (e) => {
    if (e.deltaY !== 0) {
      e.preventDefault();
      if (containerRef.value) {
        containerRef.value.scrollLeft += e.deltaY;
      }
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.tagName === "BUTTON" || e.target.closest("button")) return;

    isDown = true;
    isDragging.value = true;
    startX = e.pageX - containerRef.value.offsetLeft;
    startScrollLeft = containerRef.value.scrollLeft;
  };

  const handleMouseMove = (e) => {
    if (!isDown || !containerRef.value) return;

    e.preventDefault();
    const x = e.pageX - containerRef.value.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.value.scrollLeft = startScrollLeft - walk;
  };

  const handleMouseUp = () => {
    isDown = false;
    isDragging.value = false;
  };

  const handleMouseLeave = () => {
    isDown = false;
    isDragging.value = false;
  };

  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
    touchStartScrollLeft = containerRef.value.scrollLeft;
  };

  const handleTouchMove = (e) => {
    if (!touchStartX || !containerRef.value) return;

    const touchX = e.touches[0].clientX;
    const diff = touchStartX - touchX;
    containerRef.value.scrollLeft = touchStartScrollLeft + diff;
  };

  const handleTouchEnd = () => {
    touchStartX = 0;
  };

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // Debounced resize handler
  const debouncedUpdateScrollInfo = debounce(updateScrollInfo, 150);

  // Attach/detach listeners on a given element so they can follow the
  // container across re-renders (e.g. the loading skeleton ↔ content swap).
  const attachListeners = (container) => {
    container.addEventListener("scroll", handleScroll);
    container.addEventListener("wheel", handleWheel);
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseLeave);
    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("touchmove", handleTouchMove);
    container.addEventListener("touchend", handleTouchEnd);
  };

  const detachListeners = (container) => {
    container.removeEventListener("scroll", handleScroll);
    container.removeEventListener("wheel", handleWheel);
    container.removeEventListener("mousedown", handleMouseDown);
    container.removeEventListener("mousemove", handleMouseMove);
    container.removeEventListener("mouseup", handleMouseUp);
    container.removeEventListener("mouseleave", handleMouseLeave);
    container.removeEventListener("touchstart", handleTouchStart);
    container.removeEventListener("touchmove", handleTouchMove);
    container.removeEventListener("touchend", handleTouchEnd);
  };

  // Re-wire listeners whenever the container element changes. The scrollable
  // element is recreated when the loading skeleton swaps in/out, so a one-shot
  // onMounted would leave the new element without listeners.
  watch(
    containerRef,
    (newEl, oldEl) => {
      if (oldEl) detachListeners(oldEl);
      if (newEl) {
        attachListeners(newEl);
        updateScrollInfo();
      }
    },
    { immediate: true },
  );

  // Lifecycle
  onMounted(() => {
    window.addEventListener("resize", debouncedUpdateScrollInfo);
  });

  onUnmounted(() => {
    if (containerRef.value) detachListeners(containerRef.value);
    window.removeEventListener("resize", debouncedUpdateScrollInfo);
  });

  return {
    // Refs
    containerRef,
    isDragging,

    // Computed
    scrollPercentage,
    canScrollLeft,
    canScrollRight,

    // Methods
    scrollTo,
    scrollToPosition,
    updateScrollInfo,
  };
}

// Optional: Separate composable for arrow navigation
export function useScrollArrows(scrollFn) {
  const scrollAmount = ref(200);

  const scrollLeft = () => scrollFn(-scrollAmount.value);
  const scrollRight = () => scrollFn(scrollAmount.value);

  return {
    scrollAmount,
    scrollLeft,
    scrollRight,
  };
}
